import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { getAnalyticsData } from "../services/analyticsService.js";
import { logError } from "../../../shared/utils/logger.js";
import {
  UserOutlined,
  MessageOutlined,
  FileTextOutlined,
  ShoppingCartOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  ArrowRightOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import FunnelAnalyticsWidget from "./FunnelAnalyticsWidget.jsx";
import LeadFlowProgressWidget from "./LeadFlowProgressWidget.jsx";

/**
 * Smart Analytics Dashboard Component
 * Shows real-time user activity, journey status, and meaningful metrics
 */
const CampaignAnalytics = () => {
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState("7");

  useEffect(() => {
    fetchAnalyticsData();
    // Refresh data every 30 seconds for real-time updates
    const interval = setInterval(fetchAnalyticsData, 30000);
    return () => clearInterval(interval);
  }, [dateRange]);

  const fetchAnalyticsData = async () => {
    setLoading(true);
    try {
      const response = await getAnalyticsData({ dateRange });

      if (response.success) {
        setAnalyticsData(response.data);
      } else {
        setAnalyticsData(null);
      }
    } catch (error) {
      logError("Error fetching analytics data", {
        error: error.message,
        stack: error.stack,
      });
      setAnalyticsData(null);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (value) => {
    if (value === undefined || value === null) return "0";
    return value.toLocaleString();
  };

  const formatPercentage = (value) => {
    if (typeof value === "string") {
      return `${parseFloat(value).toFixed(1)}%`;
    }
    return `${(value * 100).toFixed(1)}%`;
  };

  const getStageIcon = (stage) => {
    switch (stage) {
      case "whatsapp_start":
        return <MessageOutlined style={{ color: "#1890ff" }} />;
      case "lead_created":
        return <UserOutlined style={{ color: "#52c41a" }} />;
      case "quote_generated":
        return <FileTextOutlined style={{ color: "#faad14" }} />;
      case "order_confirmed":
        return <ShoppingCartOutlined style={{ color: "#722ed1" }} />;
      default:
        return <UserOutlined />;
    }
  };

  const getStageColor = (stage) => {
    switch (stage) {
      case "whatsapp_start":
        return "#1890ff";
      case "lead_created":
        return "#52c41a";
      case "quote_generated":
        return "#faad14";
      case "order_confirmed":
        return "#722ed1";
      default:
        return "#8c8c8c";
    }
  };

  if (loading) {
    return <LoadingContainer>Loading smart analytics...</LoadingContainer>;
  }

  if (!analyticsData) {
    return (
      <AnalyticsContainer>
        <Header>
          <Title>Smart Analytics Dashboard</Title>
          <Subtitle>Real-time user journey tracking and insights</Subtitle>
        </Header>
        <NoDataMessage>
          <p>
            No analytics data available. Start tracking user journeys to see
            insights here.
          </p>
        </NoDataMessage>
      </AnalyticsContainer>
    );
  }

  const { funnel, campaigns } = analyticsData;

  return (
    <AnalyticsContainer>
      <Header>
        <Title>Smart Analytics Dashboard</Title>
        <Subtitle>Real-time user journey tracking and insights</Subtitle>
        <DateRangeSelector>
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
          >
            <option value="1">Last 24 Hours</option>
            <option value="7">Last 7 Days</option>
            <option value="30">Last 30 Days</option>
            <option value="90">Last 90 Days</option>
          </select>
          <button
            onClick={fetchAnalyticsData}
            disabled={loading}
            style={{
              marginLeft: "12px",
              padding: "6px 12px",
              border: "1px solid #d9d9d9",
              borderRadius: "6px",
              background: "white",
              cursor: loading ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
            title="Refresh Analytics Data"
          >
            <ReloadOutlined spin={loading} />
            {loading ? "Refreshing..." : "Refresh"}
          </button>
        </DateRangeSelector>
      </Header>

      {/* Lead Flow Progress Widget */}
      <Section>
        <LeadFlowProgressWidget showControls={true} refreshInterval={30000} />
      </Section>

      {/* Funnel Analytics Widget */}
      <Section>
        <FunnelAnalyticsWidget
          data={{
            funnel: {
              totalUsers: funnel?.totalUsers || 0,
              activeUsers: funnel?.currentActiveUsers || 0,
              completionRate: funnel?.ordersConfirmed || 0,
              conversionRates: funnel?.conversionRates || {},
              stageBreakdown: funnel?.currentStageBreakdown || {},
              journeyInsights: {
                usersStuckAtWhatsApp:
                  funnel?.currentStageBreakdown?.whatsapp_start?.count || 0,
                usersStuckAtLead:
                  funnel?.currentStageBreakdown?.lead_created?.count || 0,
                usersStuckAtQuote:
                  funnel?.currentStageBreakdown?.quote_generated?.count || 0,
              },
              recentActivity: funnel?.recentActivity || {},
            },
            insights: {
              usersStuckAtWhatsApp:
                funnel?.currentStageBreakdown?.whatsapp_start?.count || 0,
              usersStuckAtLead:
                funnel?.currentStageBreakdown?.lead_created?.count || 0,
              usersStuckAtQuote:
                funnel?.currentStageBreakdown?.quote_generated?.count || 0,
            },
            campaigns: {
              total: campaigns?.length || 0,
              topPerforming: campaigns?.slice(0, 3) || [],
              averageConversionRate:
                campaigns?.length > 0
                  ? campaigns.reduce(
                      (sum, campaign) => sum + (campaign.conversion || 0),
                      0
                    ) / campaigns.length
                  : 0,
            },
          }}
          loading={loading}
          dateRange={parseInt(dateRange)}
          onDateRangeChange={(newRange) => setDateRange(newRange.toString())}
          showControls={true}
        />
      </Section>

      {/* Real-Time User Activity */}
      <Section>
        <SectionTitle>
          <ClockCircleOutlined style={{ marginRight: 8 }} />
          Real-Time User Activity
        </SectionTitle>
        <ActivityGrid>
          <ActivityCard>
            <ActivityIcon style={{ backgroundColor: "#1890ff" }}>
              <UserOutlined />
            </ActivityIcon>
            <ActivityContent>
              <ActivityNumber>
                {formatNumber(funnel?.totalUsers || 0)}
              </ActivityNumber>
              <ActivityLabel>Active Users Today</ActivityLabel>
              <ActivityStatus>Currently Online</ActivityStatus>
            </ActivityContent>
          </ActivityCard>

          <ActivityCard>
            <ActivityIcon style={{ backgroundColor: "#52c41a" }}>
              <MessageOutlined />
            </ActivityIcon>
            <ActivityContent>
              <ActivityNumber>
                {formatNumber(funnel?.whatsappStarts || 0)}
              </ActivityNumber>
              <ActivityLabel>New Conversations</ActivityLabel>
              <ActivityStatus>Started Today</ActivityStatus>
            </ActivityContent>
          </ActivityCard>

          <ActivityCard>
            <ActivityIcon style={{ backgroundColor: "#faad14" }}>
              <FileTextOutlined />
            </ActivityIcon>
            <ActivityContent>
              <ActivityNumber>
                {formatNumber(funnel?.leadsGenerated || 0)}
              </ActivityNumber>
              <ActivityLabel>Leads Created</ActivityLabel>
              <ActivityStatus>This Period</ActivityStatus>
            </ActivityContent>
          </ActivityCard>

          <ActivityCard>
            <ActivityIcon style={{ backgroundColor: "#722ed1" }}>
              <ShoppingCartOutlined />
            </ActivityIcon>
            <ActivityContent>
              <ActivityNumber>
                {formatNumber(funnel?.ordersConfirmed || 0)}
              </ActivityNumber>
              <ActivityLabel>Orders Confirmed</ActivityLabel>
              <ActivityStatus>This Period</ActivityStatus>
            </ActivityContent>
          </ActivityCard>
        </ActivityGrid>
      </Section>

      {/* User Journey Status */}
      <Section>
        <SectionTitle>
          <ArrowRightOutlined style={{ marginRight: 8 }} />
          Current User Journey Status
        </SectionTitle>
        <JourneyGrid>
          <JourneyStage>
            <StageHeader>
              <StageIcon>{getStageIcon("whatsapp_start")}</StageIcon>
              <StageInfo>
                <StageName>WhatsApp Start</StageName>
                <StageCount>
                  {formatNumber(
                    funnel?.currentStageBreakdown?.whatsapp_start?.count || 0
                  )}{" "}
                  users
                </StageCount>
              </StageInfo>
            </StageHeader>
            <StageProgress>
              <ProgressBar
                percent={
                  funnel?.currentStageBreakdown?.whatsapp_start?.count > 0
                    ? (funnel.currentStageBreakdown.whatsapp_start.count /
                        (funnel.totalUsers || 1)) *
                      100
                    : 0
                }
                strokeColor={getStageColor("whatsapp_start")}
                showInfo={false}
              />
            </StageProgress>
            <StageStatus>
              {funnel?.currentStageBreakdown?.whatsapp_start?.count > 0 ? (
                <>
                  <ExclamationCircleOutlined
                    style={{ color: "#faad14", marginRight: 4 }}
                  />
                  {formatNumber(
                    funnel.currentStageBreakdown.whatsapp_start.count
                  )}{" "}
                  users stuck here
                </>
              ) : (
                <>
                  <CheckCircleOutlined
                    style={{ color: "#52c41a", marginRight: 4 }}
                  />
                  No users stuck here
                </>
              )}
            </StageStatus>
          </JourneyStage>

          <JourneyArrow>→</JourneyArrow>

          <JourneyStage>
            <StageHeader>
              <StageIcon>{getStageIcon("lead_created")}</StageIcon>
              <StageInfo>
                <StageName>Lead Created</StageName>
                <StageCount>
                  {formatNumber(funnel?.leadsGenerated || 0)} users
                </StageCount>
              </StageInfo>
            </StageHeader>
            <StageProgress>
              <ProgressBar
                percent={
                  funnel?.conversionRates?.whatsappToLead
                    ? parseFloat(funnel.conversionRates.whatsappToLead)
                    : 0
                }
                strokeColor={getStageColor("lead_created")}
                showInfo={false}
              />
            </StageProgress>
            <StageStatus>
              {funnel?.conversionRates?.whatsappToLead === "100.0" ? (
                <>
                  <CheckCircleOutlined
                    style={{ color: "#52c41a", marginRight: 4 }}
                  />
                  All users progressed
                </>
              ) : (
                <>
                  <ExclamationCircleOutlined
                    style={{ color: "#faad14", marginRight: 4 }}
                  />
                  {formatNumber(
                    (funnel?.whatsappStarts || 0) -
                      (funnel?.leadsGenerated || 0)
                  )}{" "}
                  users stuck here
                </>
              )}
            </StageStatus>
          </JourneyStage>

          <JourneyArrow>→</JourneyArrow>

          <JourneyStage>
            <StageHeader>
              <StageIcon>{getStageIcon("quote_generated")}</StageIcon>
              <StageInfo>
                <StageName>Quote Generated</StageName>
                <StageCount>
                  {formatNumber(funnel?.quotesGenerated || 0)} users
                </StageCount>
              </StageInfo>
            </StageHeader>
            <StageProgress>
              <ProgressBar
                percent={
                  funnel?.conversionRates?.leadToQuote
                    ? parseFloat(funnel.conversionRates.leadToQuote)
                    : 0
                }
                strokeColor={getStageColor("quote_generated")}
                showInfo={false}
              />
            </StageProgress>
            <StageStatus>
              {funnel?.conversionRates?.leadToQuote === "100.0" ? (
                <>
                  <CheckCircleOutlined
                    style={{ color: "#52c41a", marginRight: 4 }}
                  />
                  All leads quoted
                </>
              ) : (
                <>
                  <ExclamationCircleOutlined
                    style={{ color: "#faad14", marginRight: 4 }}
                  />
                  {formatNumber(
                    (funnel?.leadsGenerated || 0) -
                      (funnel?.quotesGenerated || 0)
                  )}{" "}
                  leads need quotes
                </>
              )}
            </StageStatus>
          </JourneyStage>

          <JourneyArrow>→</JourneyArrow>

          <JourneyStage>
            <StageHeader>
              <StageIcon>{getStageIcon("order_confirmed")}</StageIcon>
              <StageInfo>
                <StageName>Order Confirmed</StageName>
                <StageCount>
                  {formatNumber(funnel?.ordersConfirmed || 0)} users
                </StageCount>
              </StageInfo>
            </StageHeader>
            <StageProgress>
              <ProgressBar
                percent={
                  funnel?.conversionRates?.quoteToOrder
                    ? parseFloat(funnel.conversionRates.quoteToOrder)
                    : 0
                }
                strokeColor={getStageColor("order_confirmed")}
                showInfo={false}
              />
            </StageProgress>
            <StageStatus>
              {funnel?.conversionRates?.quoteToOrder === "100.0" ? (
                <>
                  <CheckCircleOutlined
                    style={{ color: "#52c41a", marginRight: 4 }}
                  />
                  All quotes converted
                </>
              ) : (
                <>
                  <ExclamationCircleOutlined
                    style={{ color: "#faad14", marginRight: 4 }}
                  />
                  {formatNumber(
                    (funnel?.quotesGenerated || 0) -
                      (funnel?.ordersConfirmed || 0)
                  )}{" "}
                  quotes pending
                </>
              )}
            </StageStatus>
          </JourneyStage>
        </JourneyGrid>

        <OverallMetrics>
          <MetricCard>
            <MetricLabel>Overall Success Rate</MetricLabel>
            <MetricValue style={{ color: "#52c41a" }}>
              {formatPercentage(
                funnel?.conversionRates?.overallConversion || "0"
              )}
            </MetricValue>
          </MetricCard>
          <MetricCard>
            <MetricLabel>Users in Progress</MetricLabel>
            <MetricValue style={{ color: "#1890ff" }}>
              {formatNumber(
                (funnel?.whatsappStarts || 0) - (funnel?.ordersConfirmed || 0)
              )}
            </MetricValue>
          </MetricCard>
          <MetricCard>
            <MetricLabel>Completed Journeys</MetricLabel>
            <MetricValue style={{ color: "#722ed1" }}>
              {formatNumber(funnel?.ordersConfirmed || 0)}
            </MetricValue>
          </MetricCard>
        </OverallMetrics>
      </Section>

      {/* Campaign Performance */}
      {campaigns && campaigns.length > 0 && (
        <Section>
          <SectionTitle>Campaign Performance</SectionTitle>
          <CampaignGrid>
            {campaigns.map((campaign, index) => (
              <CampaignCard
                key={campaign.id || campaign.campaignId || `campaign-${index}`}
              >
                <CampaignName
                  title={campaign._id?.campaign_id || "Direct Traffic"}
                >
                  {campaign._id?.campaign_id || "Direct Traffic"}
                </CampaignName>
                <CampaignSource title={campaign._id?.source || "Unknown"}>
                  {campaign._id?.source || "Unknown"}
                </CampaignSource>
                <CampaignMetrics>
                  <Metric>
                    <MetricLabel>Users</MetricLabel>
                    <MetricValue>
                      {formatNumber(campaign.users || 0)}
                    </MetricValue>
                  </Metric>
                  <Metric>
                    <MetricLabel>Leads</MetricLabel>
                    <MetricValue>
                      {formatNumber(campaign.leads || 0)}
                    </MetricValue>
                  </Metric>
                  <Metric>
                    <MetricLabel>Quotes</MetricLabel>
                    <MetricValue>
                      {formatNumber(campaign.quotes || 0)}
                    </MetricValue>
                  </Metric>
                  <Metric>
                    <MetricLabel>Orders</MetricLabel>
                    <MetricValue>
                      {formatNumber(campaign.orders || 0)}
                    </MetricValue>
                  </Metric>
                </CampaignMetrics>
                <CampaignConversion>
                  Conversion: {formatPercentage(campaign.conversion || "0")}
                </CampaignConversion>
              </CampaignCard>
            ))}
          </CampaignGrid>
        </Section>
      )}
    </AnalyticsContainer>
  );
};

// Styled Components
const AnalyticsContainer = styled.div`
  padding: 24px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
  flex-wrap: wrap;
  gap: 16px;
`;

const Title = styled.h2`
  margin: 0;
  color: #262626;
  font-size: 24px;
  font-weight: 600;
`;

const Subtitle = styled.p`
  margin: 0;
  color: #8c8c8c;
  font-size: 14px;
`;

const DateRangeSelector = styled.div`
  select {
    padding: 8px 16px;
    border: 1px solid #d9d9d9;
    border-radius: 6px;
    background: #fff;
    font-size: 14px;
    cursor: pointer;

    &:focus {
      outline: none;
      border-color: #1890ff;
      box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
    }
  }
`;

const Section = styled.div`
  margin-bottom: 32px;
`;

const SectionTitle = styled.h3`
  margin: 0 0 20px 0;
  color: #262626;
  font-size: 18px;
  font-weight: 600;
  display: flex;
  align-items: center;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  color: #8c8c8c;
  font-size: 16px;
`;

const NoDataMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: #8c8c8c;

  p {
    margin: 8px 0;
  }
`;

// Activity Grid Styles
const ActivityGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 20px;
`;

const ActivityCard = styled.div`
  display: flex;
  align-items: center;
  padding: 20px;
  background: #fafafa;
  border-radius: 8px;
  border: 1px solid #f0f0f0;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const ActivityIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 16px;

  svg {
    font-size: 24px;
    color: white;
  }
`;

const ActivityContent = styled.div`
  flex: 1;
`;

const ActivityNumber = styled.div`
  font-size: 24px;
  font-weight: 600;
  color: #262626;
  margin-bottom: 4px;
`;

const ActivityLabel = styled.div`
  font-size: 14px;
  color: #8c8c8c;
  margin-bottom: 4px;
`;

const ActivityStatus = styled.div`
  font-size: 12px;
  color: #52c41a;
  font-weight: 500;
`;

// Journey Grid Styles
const JourneyGrid = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 24px;
`;

const JourneyStage = styled.div`
  flex: 1;
  min-width: 200px;
  padding: 20px;
  background: #fafafa;
  border-radius: 8px;
  border: 1px solid #f0f0f0;
`;

const StageHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 16px;
`;

const StageIcon = styled.div`
  margin-right: 12px;

  svg {
    font-size: 20px;
  }
`;

const StageInfo = styled.div`
  flex: 1;
`;

const StageName = styled.div`
  font-weight: 600;
  color: #262626;
  margin-bottom: 4px;
`;

const StageCount = styled.div`
  font-size: 12px;
  color: #8c8c8c;
`;

const StageProgress = styled.div`
  margin-bottom: 12px;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 6px;
  background: #f0f0f0;
  border-radius: 3px;
  overflow: hidden;

  &::before {
    content: "";
    display: block;
    height: 100%;
    width: ${(props) => props.percent}%;
    background: ${(props) => props.strokeColor};
    transition: width 0.3s ease;
  }
`;

const StageStatus = styled.div`
  font-size: 12px;
  color: #8c8c8c;
  display: flex;
  align-items: center;
`;

const JourneyArrow = styled.div`
  font-size: 24px;
  color: #d9d9d9;
  font-weight: bold;

  @media (max-width: 768px) {
    display: none;
  }
`;

// Overall Metrics Styles
const OverallMetrics = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-top: 24px;
`;

const MetricCard = styled.div`
  text-align: center;
  padding: 20px;
  background: #fafafa;
  border-radius: 8px;
  border: 1px solid #f0f0f0;
`;

const MetricLabel = styled.div`
  font-size: 14px;
  color: #8c8c8c;
  margin-bottom: 8px;
`;

const MetricValue = styled.div`
  font-size: 24px;
  font-weight: 600;
  color: #262626;
`;

// Campaign Styles
const CampaignGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
`;

const CampaignCard = styled.div`
  padding: 20px;
  background: #fafafa;
  border-radius: 8px;
  border: 1px solid #f0f0f0;
`;

const CampaignName = styled.div`
  font-weight: 600;
  color: #262626;
  margin-bottom: 8px;
  font-size: 16px;
`;

const CampaignSource = styled.div`
  font-size: 12px;
  color: #8c8c8c;
  margin-bottom: 16px;
`;

const CampaignMetrics = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-bottom: 16px;
`;

const Metric = styled.div`
  text-align: center;
`;

const CampaignConversion = styled.div`
  text-align: center;
  font-size: 14px;
  color: #52c41a;
  font-weight: 500;
`;

export default CampaignAnalytics;
